import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Table2, Network, BarChart3, GitBranch, LogOut, FileText, Code2 } from 'lucide-react';
import { toast } from 'sonner';
import CompilerSidebar from '../components/CompilerSidebar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const workflowTypes = [
    { id: 'table', name: 'Table', icon: Table2, color: 'text-primary', bg: 'bg-primary/10' },
    { id: 'diagram', name: 'Diagram', icon: Network, color: 'text-accent-cyan', bg: 'bg-accent-cyan/10' },
    { id: 'graph', name: 'Graph', icon: BarChart3, color: 'text-accent-pink', bg: 'bg-accent-pink/10' },
    { id: 'tree', name: 'Tree', icon: GitBranch, color: 'text-accent-yellow', bg: 'bg-accent-yellow/10' }
];

function Dashboard() {
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newWorkflowName, setNewWorkflowName] = useState('');
    const [selectedType, setSelectedType] = useState('diagram');
    const [showCompiler, setShowCompiler] = useState(false);

    useEffect(() => {
        fetchWorkflows();
    }, []);

    const fetchWorkflows = async () => {
        try {
            const response = await fetch(`${API}/workflows`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setWorkflows(data);
            }
        } catch (error) {
            toast.error('Failed to load workflows');
        } finally {
            setLoading(false);
        }
    };

    const createWorkflow = async () => {
        if (!newWorkflowName.trim()) {
            toast.error('Please enter a workflow name');
            return;
        }

        try {
            const response = await fetch(`${API}/workflows`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: newWorkflowName, type: selectedType })
            });

            if (response.ok) {
                const newWorkflow = await response.json();
                toast.success('Workflow created!');
                navigate(`/workflow/${newWorkflow.workflow_id}`);
            }
        } catch (error) {
            toast.error('Failed to create workflow');
        }
    };

    return (
        <div className="min-h-screen bg-background text-white flex">
            {/* Main Content */}
            <div className={`flex-1 transition-all duration-300 ${showCompiler ? 'mr-[600px]' : ''}`}>
                {/* Navbar */}
                <nav className="fixed top-0 left-0 right-0 z-40 bg-black/50 backdrop-blur-lg border-b border-white/5">
                    <div className={`px-8 h-16 flex items-center justify-between transition-all ${showCompiler ? 'mr-[600px]' : ''}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                                <Network className="w-6 h-6 text-black" />
                            </div>
                            <span className="font-heading text-xl font-bold">FlowCode</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowCompiler(!showCompiler)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showCompiler ? 'bg-primary text-black' : 'bg-white/5 hover:bg-white/10'
                                    }`}
                            >
                                <Code2 className="w-4 h-4" />
                                {showCompiler ? 'Hide' : 'Show'} Compiler
                            </button>
                            <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                                <span className="text-xs font-bold text-primary tracking-widest uppercase">Public Mode</span>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Main Content Area */}
                <div className="pt-24 px-8 pb-12">
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-12"
                        >
                            <h1 className="font-heading text-5xl font-bold tracking-tight mb-4">
                                Your Workflows
                            </h1>
                            <p className="text-lg text-text-secondary">
                                Create and manage your visual workflows
                            </p>
                        </motion.div>

                        {/* Workflow Type Cards */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="grid md:grid-cols-4 gap-4 mb-12"
                        >
                            {workflowTypes.map((type, index) => (
                                <button
                                    key={type.id}
                                    onClick={() => {
                                        setSelectedType(type.id);
                                        setShowCreateModal(true);
                                    }}
                                    className="glass-card p-6 hover:bg-white/10 transition-all group text-left"
                                >
                                    <div className={`w-12 h-12 rounded-xl ${type.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                        <type.icon className={`w-6 h-6 ${type.color}`} />
                                    </div>
                                    <h3 className="font-heading text-xl font-medium mb-2">{type.name}</h3>
                                    <p className="text-sm text-text-secondary">Create a {type.name.toLowerCase()} workflow</p>
                                </button>
                            ))}
                        </motion.div>

                        {/* Workflows List */}
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-heading text-2xl font-medium">Recent Workflows</h2>
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="flex items-center gap-2 px-6 py-2 bg-primary text-black rounded-full hover:bg-primary-hover transition-colors font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    New Workflow
                                </button>
                            </div>

                            {loading ? (
                                <div className="glass-card p-12 text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                                    <p className="text-text-secondary">Loading workflows...</p>
                                </div>
                            ) : workflows.length === 0 ? (
                                <div className="glass-card p-12 text-center">
                                    <FileText className="w-16 h-16 text-text-muted mx-auto mb-4" />
                                    <h3 className="font-heading text-xl font-medium mb-2">No workflows yet</h3>
                                    <p className="text-text-secondary mb-6">Create your first workflow to get started</p>
                                    <button
                                        onClick={() => setShowCreateModal(true)}
                                        className="neon-button"
                                    >
                                        Create Workflow
                                    </button>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-3 gap-4">
                                    {workflows.map((workflow) => {
                                        const typeConfig = workflowTypes.find(t => t.id === workflow.type) || workflowTypes[0];
                                        return (
                                            <motion.button
                                                key={workflow.workflow_id}
                                                onClick={() => navigate(`/workflow/${workflow.workflow_id}`)}
                                                className="glass-card p-6 text-left hover:bg-white/10 transition-all group"
                                                whileHover={{ y: -4 }}
                                            >
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className={`w-12 h-12 rounded-xl ${typeConfig.bg} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                                        <typeConfig.icon className={`w-6 h-6 ${typeConfig.color}`} />
                                                    </div>
                                                    <span className="text-xs text-text-muted capitalize">{workflow.type}</span>
                                                </div>
                                                <h3 className="font-heading text-lg font-medium mb-2">{workflow.name}</h3>
                                                <p className="text-sm text-text-secondary">
                                                    {workflow.nodes?.length || 0} nodes • {workflow.connections?.length || 0} connections
                                                </p>
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Compiler Sidebar */}
            {showCompiler && (
                <CompilerSidebar onClose={() => setShowCompiler(false)} />
            )}

            {/* Create Workflow Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-card p-8 max-w-md w-full"
                    >
                        <h2 className="font-heading text-2xl font-bold mb-6">Create New Workflow</h2>

                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2">Workflow Name</label>
                            <input
                                type="text"
                                value={newWorkflowName}
                                onChange={(e) => setNewWorkflowName(e.target.value)}
                                placeholder="Enter workflow name"
                                className="glass-input w-full"
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-3">Workflow Type</label>
                            <div className="grid grid-cols-2 gap-3">
                                {workflowTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        onClick={() => setSelectedType(type.id)}
                                        className={`p-4 rounded-lg border transition-all ${selectedType === type.id
                                            ? 'border-primary bg-primary/10'
                                            : 'border-white/10 bg-white/5 hover:bg-white/10'
                                            }`}
                                    >
                                        <type.icon className={`w-6 h-6 ${selectedType === type.id ? type.color : 'text-text-secondary'} mx-auto mb-2`} />
                                        <p className="text-sm font-medium">{type.name}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewWorkflowName('');
                                }}
                                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                                data-testid="cancel-create-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={createWorkflow}
                                className="flex-1 neon-button"
                                data-testid="confirm-create-btn"
                            >
                                Create
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;
